import { ROLE_LABELS, TEAM_LEVEL_LABELS, notificationAddress, type TeamMember } from '@easyhelp/shared';
import CableOutlinedIcon from '@mui/icons-material/CableOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useId, useState } from 'react';
import { TARGET_SX } from '../../../components/accessible-buttons';
import { MemberMcpConnections } from './MemberMcpConnections';
import { workloadLabel } from './workload';

export interface TeamMemberItemProps {
  member: TeamMember;
  onEdit: (member: TeamMember) => void;
  onMove: (member: TeamMember) => void;
  onRevokeSessions: (member: TeamMember) => void;
  onRemove: (member: TeamMember) => void;
  /**
   * Presente solo con l'MCP acceso sull'istanza (`features.mcp`): la riga ha il comando che apre le
   * connessioni di Claude e Cursor del membro (#152); `onRevoked` riceve l'esito della revoca.
   */
  mcp?: { onRevoked: (message: string) => void };
}

/**
 * Riga di un membro: identità, indirizzo di notifica, ruolo, carico e azioni. Con l'MCP acceso il
 * comando «Connessioni di Claude e Cursor» apre sotto la riga le connessioni del membro (#152).
 */
export function TeamMemberItem({ member, onEdit, onMove, onRevokeSessions, onRemove, mcp }: TeamMemberItemProps) {
  const otherLevel = member.level === 'primo' ? 'secondo' : 'primo';
  const notifyTo = notificationAddress(member);
  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const connectionsId = useId();

  return (
    <ListItem divider sx={{ gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <ListItemAvatar sx={{ mt: 1 }}>
        <Avatar sx={{ bgcolor: 'primary.main' }}>{member.name.charAt(0).toUpperCase()}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Typography component="span" fontWeight={500}>
              {member.name}
            </Typography>
            <Chip size="small" label={ROLE_LABELS[member.role]} variant="outlined" />
          </Stack>
        }
        secondary={
          <Stack component="span" spacing={0.5}>
            <Typography component="span" variant="body2" color="text.secondary">
              {member.email}
              {member.notificationEmail && ` · notifiche a ${notifyTo}`}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              color={member.openTicketCount > 0 ? 'text.primary' : 'text.secondary'}
            >
              {workloadLabel(member.openTicketCount)}
            </Typography>
          </Stack>
        }
        slotProps={{ secondary: { component: 'div' } }}
        sx={{ flex: '1 1 240px', m: 0 }}
      />
      {/* Sotto sm le azioni scendono su una riga propria invece di coprire email e carico. */}
      <Stack direction="row" spacing={1} sx={{ flexBasis: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-end' }}>
        {mcp && (
          <Tooltip title="Connessioni di Claude e Cursor">
            <IconButton
              aria-label={`Connessioni di Claude e Cursor di ${member.name}`}
              aria-expanded={connectionsOpen}
              aria-controls={connectionsId}
              onClick={() => setConnectionsOpen((open) => !open)}
              color={connectionsOpen ? 'primary' : 'default'}
              sx={TARGET_SX}
            >
              <CableOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Modifica">
          <IconButton aria-label={`Modifica ${member.name}`} onClick={() => onEdit(member)}>
            <EditOutlinedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={`Sposta al ${TEAM_LEVEL_LABELS[otherLevel]}`}>
          <IconButton
            aria-label={`Sposta ${member.name} al ${TEAM_LEVEL_LABELS[otherLevel]}`}
            onClick={() => onMove(member)}
          >
            <SwapVertIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Chiudi le sessioni">
          <IconButton aria-label={`Chiudi le sessioni di ${member.name}`} onClick={() => onRevokeSessions(member)}>
            <LogoutIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Rimuovi dal team">
          <IconButton aria-label={`Rimuovi ${member.name} dal team`} color="error" onClick={() => onRemove(member)}>
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {mcp && (
        // Montato solo da aperto: l'elenco si legge quando serve, e chiuso non resta nel tab order.
        <Box
          id={connectionsId}
          role="region"
          aria-label={`Connessioni di Claude e Cursor di ${member.name}`}
          hidden={!connectionsOpen}
          sx={{ flexBasis: '100%', pl: { sm: 7 } }}
        >
          {connectionsOpen && <MemberMcpConnections member={member} onRevoked={mcp.onRevoked} />}
        </Box>
      )}
    </ListItem>
  );
}
